import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import solutionDetails from '../data/solutionDetails';
import { solutions } from '../data/mockData';
import MediaElement from '../components/MediaElement';

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const SolutionDetailPage = () => {
  const { solutionId } = useParams();
  const detail = solutionDetails[solutionId];
  const solution = solutions.find((s) => s.id === solutionId);
  const [activeTab, setActiveTab] = useState({});
  const [pageVisible, setPageVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setPageVisible(true), 50);
    if (detail) {
      document.title = `${detail.heroTitle.split('\n')[0]} — Insust Global`;
    }
    return () => clearTimeout(timer);
  }, [solutionId, detail]);

  if (!detail || !solution) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Solution Not Found</h1>
          <Link to="/" className="text-teal-400 hover:text-teal-300">
            &larr; Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const getTab = (serviceIdx) => activeTab[serviceIdx] || 'capabilities';
  const setTab = (serviceIdx, tab) => setActiveTab((prev) => ({ ...prev, [serviceIdx]: tab }));

  return (
    <div className={`min-h-screen bg-slate-950 transition-opacity duration-700 ${pageVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 h-16 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.jpeg" alt="Insust Global logo" className="h-8 w-8 rounded-full object-contain" />
          <span className="text-lg font-bold text-white">Insust Global</span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </nav>

      {/* Hero */}
      <section className="min-h-screen grid lg:grid-cols-2 pt-16 relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="flex flex-col justify-center px-6 lg:px-16 py-16 relative z-10">
          <span className="inline-flex items-center gap-3 text-xs font-mono tracking-[0.18em] uppercase text-teal-400 mb-6">
            <span className="w-8 h-px bg-teal-400" />
            {detail.heroEyebrow || 'Explore Solutions'}
          </span>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-[1.08]">
            {detail.heroTitle.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line.includes(detail.heroTitleAccent) ? (
                  <>
                    {line.split(detail.heroTitleAccent)[0]}
                    <em className="text-teal-400 italic">{detail.heroTitleAccent}</em>
                    {line.split(detail.heroTitleAccent)[1]}
                  </>
                ) : (
                  line
                )}
                {i < detail.heroTitle.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
          <p className="text-base lg:text-lg text-slate-400 leading-relaxed max-w-lg mb-8">
            {detail.heroSub}
          </p>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white font-semibold text-sm uppercase tracking-wider transition-all">
              Explore Services
            </button>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-slate-600 text-white hover:border-teal-400 hover:text-teal-400 font-semibold text-sm uppercase tracking-wider transition-all">
              Talk to an Expert
            </Link>
          </div>
        </div>
        <div className="relative overflow-hidden bg-slate-950 min-h-[320px] lg:min-h-0">
          <MediaElement
            src={detail.heroImage}
            alt={detail.heroTitle}
            className="w-full h-full object-contain opacity-60 hover:scale-[1.02] transition-transform duration-[8s]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-transparent to-transparent opacity-80 lg:opacity-100" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
          {detail.heroBadgeValue && (
            <div className="absolute bottom-8 left-8 bg-slate-950/85 backdrop-blur-sm px-5 py-3 border-l-[3px] border-teal-400">
              <div className="text-2xl font-bold text-white">{detail.heroBadgeValue}</div>
              <div className="text-xs font-mono text-slate-400 tracking-wider">{detail.heroBadgeLabel}</div>
            </div>
          )}
        </div>
      </section>

      {/* Stats */}
      {detail.stats && detail.stats.length > 0 && (
        <section className="bg-slate-900 border-y border-slate-800">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 px-6 lg:px-16 py-10">
            {detail.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-teal-400">{stat.value}</div>
                <div className="text-xs font-mono tracking-wider uppercase text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Section Header */}
      <section className="text-center py-16 px-6" id="services">
        <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 mb-4 block">
          {detail.sectionEyebrow}
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-white max-w-2xl mx-auto mb-4">
          {detail.sectionTitle}
        </h2>
        <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
          {detail.sectionDescription}
        </p>
      </section>

      {/* Services */}
      {detail.services.map((service, idx) => {
        const isReversed = idx % 2 !== 0;
        const bgClass = idx % 2 === 0 ? 'bg-slate-950' : 'bg-slate-900/50';
        const currentTab = getTab(idx);

        return (
          <FadeInSection key={idx}>
            {/* Divider */}
            <div className="flex items-center justify-center px-6 lg:px-16 pt-10 gap-4">
              <div className="flex-1 h-px bg-slate-800" />
              <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-slate-600 whitespace-nowrap">
                {service.number} — {service.eyebrow}
              </span>
              <div className="flex-1 h-px bg-slate-800" />
            </div>

            <section className={`${bgClass} border-t border-slate-800/50 relative overflow-hidden`}>
              <div className="absolute inset-0 pointer-events-none opacity-[0.015]"
                style={{
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)',
                  backgroundSize: '28px 28px',
                }}
              />
              <div className={`grid lg:grid-cols-2 min-h-[540px] ${isReversed ? 'lg:[direction:rtl]' : ''}`}>
                {/* Image */}
                <div className="relative overflow-hidden bg-slate-950 min-h-[280px] lg:min-h-0 [direction:ltr]">
                  {Array.isArray(service.image) ? (
                    <div className="absolute inset-0 grid grid-rows-3 gap-1">
                      {service.image.map((mediaSrc, mi) => (
                        <div key={mi} className="relative bg-slate-950 overflow-hidden">
                          <MediaElement
                            src={mediaSrc}
                            alt={`${service.title} ${mi + 1}`}
                            className="absolute inset-0 w-full h-full object-contain opacity-60 hover:scale-[1.03] transition-transform duration-700"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <MediaElement
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-contain opacity-60 hover:scale-[1.03] transition-transform duration-700"
                    />
                  )}
                  <div className={`absolute inset-0 ${
                    isReversed
                      ? 'bg-gradient-to-l from-transparent via-transparent to-slate-950/80'
                      : 'bg-gradient-to-r from-transparent via-transparent to-slate-950/80'
                  }`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
                  <span className="absolute top-5 left-5 text-[10px] font-mono tracking-[0.2em] text-slate-400 bg-slate-950/50 backdrop-blur-sm px-3 py-1">
                    {service.number} / {service.totalCount}
                  </span>
                  <span className="absolute bottom-5 left-5 bg-teal-600 text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1.5">
                    {service.tag}
                  </span>
                </div>

                {/* Content */}
                <div className="px-6 lg:px-12 py-10 lg:py-14 flex flex-col justify-center [direction:ltr]">
                  <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-teal-400 mb-3">
                    {service.eyebrow}
                  </span>
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-lg">
                    {service.lead}
                  </p>

                  {/* Tabs */}
                  <div className="flex mb-4 border border-slate-700/50 w-fit" role="tablist" aria-label={`${service.title} details`}>
                    {['capabilities', 'outcomes', 'impact'].map((tab) => (
                      <button
                        key={tab}
                        role="tab"
                        aria-selected={currentTab === tab}
                        onClick={() => setTab(idx, tab)}
                        className={`text-[10px] font-mono tracking-wider uppercase px-4 py-2 transition-all ${
                          currentTab === tab
                            ? 'bg-teal-600 text-white'
                            : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  {/* Tab Content */}
                  {currentTab === 'capabilities' && (
                    <ul className="space-y-0 animate-fade-in" role="tabpanel">
                      {service.capabilities.map((cap, ci) => (
                        <li key={ci} className="flex items-start gap-3 py-3 border-b border-slate-800/50 text-slate-400 hover:text-slate-200 hover:bg-slate-800/20 transition-colors rounded-sm px-1 -mx-1">
                          <span className="text-teal-400 font-mono mt-0.5 flex-shrink-0">&rarr;</span>
                          <div>
                            <strong className="text-white text-sm block">{cap.title}</strong>
                            <span className="text-xs text-slate-500 leading-relaxed">{cap.description}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {currentTab === 'outcomes' && (
                    <ul className="space-y-0 animate-fade-in" role="tabpanel">
                      {service.outcomes.map((out, oi) => (
                        <li key={oi} className="flex items-start gap-3 py-3 border-b border-slate-800/50 text-slate-400 hover:text-slate-200 hover:bg-slate-800/20 transition-colors rounded-sm px-1 -mx-1">
                          <span className="text-teal-400 text-[8px] mt-1.5 flex-shrink-0">&#9670;</span>
                          <span className="text-sm">{out}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {currentTab === 'impact' && (
                    <ul className="space-y-0 animate-fade-in" role="tabpanel">
                      {service.impact.map((imp, ii) => (
                        <li key={ii} className="flex items-start gap-3 py-3 border-b border-slate-800/50 text-slate-400 hover:text-slate-200 hover:bg-slate-800/20 transition-colors rounded-sm px-1 -mx-1">
                          <span className="text-teal-400 text-[10px] mt-1 flex-shrink-0">&#9673;</span>
                          <div>
                            <strong className="text-white text-sm block">{imp.title}</strong>
                            <span className="text-xs text-slate-500 leading-relaxed">{imp.description}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>
          </FadeInSection>
        );
      })}

      {/* Process */}
      {detail.process && (
        <section className="bg-slate-900 border-t border-slate-800 py-20 px-6 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 mb-3 block">How We Work</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white">{detail.process.title}</h2>
            </div>
            <div className="relative">
              <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-px bg-slate-700" />
              <div className="grid lg:grid-cols-5 gap-8 lg:gap-4">
                {(detail.process.steps || []).map((step, si) => (
                  <div key={si} className="text-center group">
                    <div className="w-16 h-16 rounded-full bg-slate-950 border-2 border-teal-600 flex items-center justify-center mx-auto mb-4 relative z-10 group-hover:bg-teal-600 transition-colors">
                      <span className="text-sm font-mono text-teal-400 group-hover:text-white transition-colors">{step.number}</span>
                    </div>
                    <h4 className="text-sm font-bold text-white mb-2">{step.title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Standards / Deliverables */}
      {detail.standards && (
        <section className="bg-slate-950 border-t border-slate-800 py-20 px-6 lg:px-16">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 mb-3 block">
                {detail.standards.eyebrow}
              </span>
              <h2 className="text-3xl font-bold text-white mb-4">{detail.standards.title}</h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">{detail.standards.description}</p>
              {detail.standards.documentation ? (
                <a
                  href={detail.standards.documentation.href}
                  {...(detail.standards.documentation.download ? { download: true } : { target: '_blank', rel: 'noopener noreferrer' })}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-teal-600 hover:bg-teal-500 text-white font-semibold text-sm uppercase tracking-wider transition-all"
                >
                  {detail.standards.documentation.label || 'Get Documentation'}
                </a>
              ) : (
                <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 bg-teal-600 hover:bg-teal-500 text-white font-semibold text-sm uppercase tracking-wider transition-all">
                  Get Documentation
                </Link>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {(detail.standards.cards || []).map((card, ci) => (
                <FadeInSection key={ci} delay={ci * 120}>
                  <div className="bg-slate-900 p-5 border border-slate-800 border-l-[3px] border-l-teal-600">
                    <h4 className="text-sm font-bold text-white mb-2">{card.title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{card.description}</p>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-teal-700 py-20 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            {detail.cta?.title || 'Ready to Get Started?'}
          </h2>
          <p className="text-base text-teal-100/70 mb-8 leading-relaxed">
            {detail.cta?.description || 'Talk to our team about how we can help.'}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/" className="px-8 py-3 bg-white hover:bg-slate-100 text-teal-700 font-bold text-sm uppercase tracking-wider transition-colors">
              Start a Project
            </Link>
            <Link to="/" className="px-8 py-3 border-2 border-white/50 hover:border-white text-white font-bold text-sm uppercase tracking-wider transition-colors">
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-6 px-6 lg:px-16 flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Insust Global. All rights reserved.</span>
        <div className="flex gap-4 text-xs text-slate-600">
          <Link to="/privacy" className="hover:text-teal-400 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-teal-400 transition-colors">Terms of Service</Link>
          <Link to="/" className="hover:text-teal-400 transition-colors">Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default SolutionDetailPage;
